import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { QuestionBankItem } from '../../../../../core/interfaces/question-search';

/**
 * Panneau de décision de relecture.
 *
 * Composant **présentationnel** : il ne connaît ni le service de relecture ni
 * l'API. Il collecte une intention (approuver / rejeter) et un motif, puis
 * l'émet. C'est l'écran parent qui persiste la décision.
 *
 * Le motif est obligatoire pour rejeter — le back renvoie un 400 sans lui. On
 * ne laisse pas partir la requête : le bouton reste désactivé tant que le champ
 * est vide, plutôt que d'attendre l'erreur serveur pour prévenir le relecteur.
 */
@Component({
  selector: 'app-review-decision',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './review-decision.component.html',
  styleUrl: './review-decision.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewDecisionComponent {
  private readonly current = signal<QuestionBankItem | null>(null);

  /**
   * Question en cours de relecture. Changer de question remet le motif à zéro :
   * un commentaire saisi pour l'item précédent n'a plus de sens sur le suivant.
   */
  @Input()
  set question(value: QuestionBankItem | null) {
    const previousId = this.current()?.id ?? null;
    this.current.set(value);
    if ((value?.id ?? null) !== previousId) {
      this.comment.set('');
      this.rejectAttempted.set(false);
    }
  }

  get question(): QuestionBankItem | null {
    return this.current();
  }

  /** Vrai pendant l'appel réseau du parent : neutralise boutons et raccourcis. */
  @Input() submitting = false;

  /** Approbation. Le motif est facultatif : `undefined` quand le champ est vide. */
  @Output() approve = new EventEmitter<string | undefined>();

  /** Rejet. Le motif est toujours renseigné — jamais émis autrement. */
  @Output() reject = new EventEmitter<string>();

  @ViewChild('commentField')
  private commentField?: ElementRef<HTMLTextAreaElement>;

  readonly comment = signal('');

  /** Passe à `true` quand un rejet est tenté sans motif. */
  readonly rejectAttempted = signal(false);

  readonly canReject = computed(() => this.comment().trim().length > 0);

  onCommentInput(event: Event): void {
    this.comment.set((event.target as HTMLTextAreaElement).value);
  }

  onApprove(): void {
    if (!this.question || this.submitting) {
      return;
    }
    const motive = this.comment().trim();
    this.approve.emit(motive.length > 0 ? motive : undefined);
  }

  onReject(): void {
    if (!this.question || this.submitting) {
      return;
    }
    if (!this.canReject()) {
      // Déclenché par le raccourci clavier : le bouton, lui, est désactivé.
      this.rejectAttempted.set(true);
      this.commentField?.nativeElement.focus();
      return;
    }
    this.reject.emit(this.comment().trim());
  }

  /**
   * Raccourcis de décision : `A` approuver, `R` rejeter.
   *
   * Ils sont portés par ce composant et non par l'écran parent parce que c'est
   * lui qui détient le motif et sa validité. Ils restent inertes tant que le
   * focus est dans un champ de saisie — sans quoi taper « rapide » dans le
   * commentaire rejetterait la question.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (isEditableTarget(event.target)) {
      return;
    }
    if (!this.question || this.submitting) {
      return;
    }

    const key = event.key.toLowerCase();
    if (key === 'a') {
      event.preventDefault();
      this.onApprove();
    } else if (key === 'r') {
      event.preventDefault();
      this.onReject();
    }
  }
}

/**
 * Vrai quand l'événement provient d'une zone de saisie.
 *
 * Dupliqué dans l'écran parent : les deux composants écoutent `document` de
 * façon indépendante et le périmètre de la tâche interdit d'ajouter un fichier
 * utilitaire partagé.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || !element.tagName) {
    return false;
  }
  const tag = element.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    element.isContentEditable === true
  );
}
